/**
 * Parses and sanitizes CEX DME deep link URL parameters (CEFIND-6099).
 * Only the allowlisted parameters below are ever read; any other query
 * params present on the URL are ignored and never leak into app state.
 */

import { Constants } from "../common/Constants";

export interface DeeplinkParams {
  searchTerm: string;
  radius?: number;
}

const DME_PARAM = 'DME';
const SEARCH_TERM_PARAM = 'searchSuggestionEN';

// Allowlist of characters expected in a DME category search term
// (e.g. "CPAP & Sleep Support", "Braces & Splints"). Anything else,
// including HTML/script injection characters, is stripped out.
const UNSAFE_SEARCH_TERM_CHARS = /[^a-zA-Z0-9 &'-]/g;

const sanitizeSearchTerm = (value: string): string =>
  value.replace(UNSAFE_SEARCH_TERM_CHARS, '').trim();

/**
 * Returns typed, sanitized deep link params when the CEX DME deep link
 * "contract" (preSelectFilter=true, DME=true, non-empty searchTerm) is met,
 * otherwise returns null so callers fall back to a normal search.
 */
export const parseDeeplinkParams = (
  urlParams: URLSearchParams,
  enableDMEDeeplink: boolean,
): string | null => {
  if (
    urlParams.get(Constants.PRE_SELECT_FILTERS) !== 'true' ||
    urlParams.get(DME_PARAM) !== 'true' ||
    !enableDMEDeeplink
  ) {
    return null;
  }

  const searchTerm = sanitizeSearchTerm(urlParams.get(SEARCH_TERM_PARAM) ?? '');
  if (!searchTerm) {
    return null;
  }

  return searchTerm;
};
